/**
 * useActivitiesBalanceKpi - Hook para KPIs do Balance Card de Atividades
 * Utiliza RPC get_activities_balance_kpi com suporte a filtros
 * 
 * @version 1.2.0
 * RELEASE: 2026-02-09 v1.2.0 - Adicionado suporte a filtros: workspace, seller, user, status
 * RELEASE: 2026-02-09 v1.1.0 - Adicionado suporte a filtro onlyMine (role-based visibility)
 * RELEASE: 2026-02-09 v1.0.0 - Implementação inicial do hook com suporte a período
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { format } from 'date-fns';
import { createLogger } from '@/lib/logger';
import type { ActivityStatusFilter } from '@/components/crm/filters/CrmActivityStatusFilter';

// Logger com namespace para este hook
const logger = createLogger('ActivitiesBalanceKpi');

/**
 * Interface dos dados retornados pela RPC
 */
export interface BalanceKpiData {
  /** Total de atividades criadas no período */
  totalActivities: number;
  /** Total de atividades pendentes (qualquer data) */
  pendingCount: number;
  /** Atividades concluídas no período */
  completedCount: number;
  /** Porcentagem de conclusão */
  completionPercentage: number;
  /** Variação de pendentes vs mês anterior */
  pendingComparison: number;
  /** Variação de concluídas vs mês anterior */
  completedComparison: number;
}

/**
 * Dados padrão para estado inicial/erro
 */
const DEFAULT_KPI_DATA: BalanceKpiData = {
  totalActivities: 0,
  pendingCount: 0,
  completedCount: 0,
  completionPercentage: 0,
  pendingComparison: 0,
  completedComparison: 0,
};

/**
 * Opções do hook de KPIs
 */
export interface ActivitiesBalanceKpiOptions {
  /** Data inicial do período */
  startDate?: Date;
  /** Data final do período */
  endDate?: Date;
  /** Workspace do negócio (null = todos) */
  workspaceId?: string | null;
  /** Vendedor do negócio (null = todos) */
  sellerId?: string | null;
  /** Responsável pela atividade (null = todos) */
  performedByUserId?: string | null;
  /** Status da atividade: 'all', 'open', 'completed' */
  status?: ActivityStatusFilter;
}

/**
 * Hook para buscar KPIs do Balance Card de atividades
 * 
 * @param options - Filtros de período, workspace, seller, user e status
 * @returns Dados de KPI, estado de loading e função para invalidar cache
 */
export function useActivitiesBalanceKpi(options?: ActivitiesBalanceKpiOptions) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  
  // Extrai opções com defaults
  const startDate = options?.startDate;
  const endDate = options?.endDate;
  const workspaceId = options?.workspaceId ?? null;
  const sellerId = options?.sellerId ?? null;
  const performedByUserId = options?.performedByUserId ?? null;
  const status = options?.status ?? 'all';

  // Formata datas para enviar à RPC (formato YYYY-MM-DD)
  const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : null;
  const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : null;

  // Query key inclui todos os filtros para refetch automático ao mudar
  const queryKey = [
    'activities-balance-kpi',
    currentTenant?.id,
    formattedStartDate,
    formattedEndDate,
    workspaceId,
    sellerId,
    performedByUserId,
    status,
  ];

  const {
    data: kpiData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async (): Promise<BalanceKpiData> => {
      if (!currentTenant?.id) {
        logger.debug('Sem tenant, retornando dados padrão');
        return DEFAULT_KPI_DATA;
      }

      logger.debug('Buscando KPIs', {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        workspaceId,
        sellerId,
        performedByUserId,
        status,
      });

      // v1.2.0: Passa novos parâmetros de filtro
      // Nota: Cast necessário até regeneração dos tipos do Supabase
      const { data, error } = await supabase.rpc('get_activities_balance_kpi', {
        p_start_date: formattedStartDate,
        p_end_date: formattedEndDate,
        p_workspace_id: workspaceId,
        p_seller_id: sellerId,
        p_user_id: performedByUserId,
        p_status: status === 'all' ? null : status,
      } as Record<string, unknown>);

      if (error) {
        logger.error('Erro na RPC', { error: error.message });
        throw error;
      }

      // A RPC retorna um array, pegamos o primeiro registro
      const result = Array.isArray(data) && data.length > 0 ? data[0] : null;

      if (!result) {
        logger.debug('RPC retornou vazio');
        return DEFAULT_KPI_DATA;
      }

      // Transforma os nomes dos campos (snake_case -> camelCase)
      const transformed: BalanceKpiData = {
        totalActivities: Number(result.total_activities) || 0,
        pendingCount: Number(result.pending_count) || 0,
        completedCount: Number(result.completed_count) || 0,
        completionPercentage: Number(result.completion_percentage) || 0,
        pendingComparison: Number(result.pending_comparison) || 0,
        completedComparison: Number(result.completed_comparison) || 0,
      };

      logger.debug('KPIs carregados', transformed);
      return transformed;
    },
    enabled: !!currentTenant?.id,
    staleTime: 60 * 1000, // 1 minuto
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  /**
   * Invalida o cache para forçar refetch dos KPIs
   * Útil quando uma atividade é criada/concluída
   */
  const invalidateKpi = () => {
    queryClient.invalidateQueries({
      queryKey: ['activities-balance-kpi'],
    });
  };

  return {
    kpiData: kpiData ?? DEFAULT_KPI_DATA,
    isLoading,
    error,
    refetch,
    invalidateKpi,
  };
}
